import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { getOrCreateChatRoomV2 } from '../../graphql/messaging';
import { listChatRoomsByHost, listChatRoomsByGuest } from '../../graphql/messaging';

/**
 * Custom hook to manage chat room creation and retrieval
 * @returns {Object} Functions and state for chat room management
 */
const useCreateChatRoom = () => {
  // Get userId from localStorage instead of context
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use the new getOrCreateChatRoomV2 mutation
  const [getOrCreateChatRoomMutation] = useMutation(getOrCreateChatRoomV2);

  /**
   * Check if a chat room exists for a studio
   * Note: With the new backend, getOrCreateChatRoomV2 handles this automatically
   * This function is kept for backward compatibility but is no longer needed
   * @param {string} targetUserId - The ID of the user to check for existing chat rooms (deprecated)
   * @param {string} studioId - The ID of the studio related to this chat
   * @returns {Promise<{chatRoomId: string}>} - Returns chatRoomId if exists, null otherwise
   */
  const checkExistingChatRoom = useCallback(async (targetUserId, studioId) => {
    // The new backend automatically handles checking for existing chat rooms
    // This function is kept for backward compatibility
    // In practice, getOrCreateChatRoomV2 will return existing chat room if one exists
    return null;
  }, []);

  /**
   * Creates a new chat room or retrieves an existing one for a studio
   * Uses the new getOrCreateChatRoomV2 mutation which handles everything automatically
   * @param {Object} params - Parameters for creating chat room
   * @param {string} params.targetUserId - User ID of the chat partner (deprecated, not needed)
   * @param {string} params.targetUserFirstName - First name of the chat partner (deprecated)
   * @param {string} params.targetUserProfilePicture - Profile picture URL of the chat partner (deprecated)
   * @param {Object} params.studio - Studio information
   * @param {string} params.studio.id - ID of the studio
   * @param {string} params.studio.StudioName - Name of the studio (deprecated)
   * @param {string} params.studio.StudioImageKey - Image key for the studio (deprecated)
   * @returns {Promise<Object>} - Chat room data including ID and partner info
   */
  const createChatRoomBasedOnUser = useCallback(async ({
    targetUserId,
    targetUserFirstName,
    targetUserProfilePicture,
    studio
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate required parameters
      if (!studio?.id) {
        throw new Error('studio.id is required but was null/undefined');
      }
      if (!userId) {
        throw new Error('current userId is required but was null/undefined');
      }

      // Use the new getOrCreateChatRoomV2 mutation
      // This automatically handles checking for existing chat rooms and creating new ones
      const result = await getOrCreateChatRoomMutation({
        variables: {
          input: {
            studioID: studio.id
          }
        }
      });

      const chatRoom = result?.data?.getOrCreateChatRoom;
      if (!chatRoom) {
        throw new Error('Failed to get or create chat room - no data returned');
      }

      // Determine chat partner based on whether user is host or guest
      const isHost = chatRoom.hostID === userId;
      const chatPartner = isHost ? chatRoom.guest : chatRoom.host;

      return {
        chatRoomId: chatRoom.id,
        chatPartner: chatPartner ? {
          id: chatPartner.id,
          firstName: chatPartner.firstName,
          profilePicture: chatPartner.profilePicture
        } : null,
        studio: chatRoom.studio
      };
    } catch (err) {
      const errorMsg = err?.graphQLErrors?.[0]?.message || err?.message || 'Unbekannter Fehler';
      setError(errorMsg);
      console.error('[createChatRoomBasedOnUser] Error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId, getOrCreateChatRoomMutation]);

  // For compatibility with the ChatLink component
  const createNewChatRoom = useCallback(async (
    studioId,
    studioName,
    studioImage,
    currentUserID,
    targetUserId
  ) => {
    // Validate parameters
    if (!studioId) {
      throw new Error('studioId is required');
    }
    if (!userId) {
      throw new Error('Current user ID is required');
    }

    // Use the new getOrCreateChatRoomV2 mutation
    // It automatically handles checking for existing chat rooms
    const result = await getOrCreateChatRoomMutation({
      variables: {
        input: {
          studioID: studioId
        }
      }
    });

    const chatRoom = result?.data?.getOrCreateChatRoom;
    if (!chatRoom) {
      throw new Error('Failed to get or create chat room');
    }
    
    return chatRoom.id;
  }, [getOrCreateChatRoomMutation, userId]);

  return {
    createChatRoomBasedOnUser,
    createNewChatRoom,
    loading,
    error
  };
};

export default useCreateChatRoom;